import React, { FC, useCallback } from 'react';
import memoize from 'memoizee';
import { useDebounceCallback } from 'usehooks-ts';

import { MidotRoleDTO, useGetRolesHook } from '@/api/generated';
import DropdownSelect, { DropdownSelectOption } from '@/components/fields/DropdownSelect';

const DEBOUNCE_DELAY = 300;

type RoleDropdownSelectProps = Omit<React.ComponentProps<typeof DropdownSelect>, 'options'>;

const filterByIds = (options: DropdownSelectOption[], ids?: string[]) => {
  if (!ids) return [];

  const idsSet = new Set(ids);

  return options.filter(option => idsSet.has(option.value));
};

const RoleDropdownSelect: FC<RoleDropdownSelectProps> = props => {
  const getRolesBySearch = useGetRolesHook();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadRoleOptions = useCallback(
    memoize(
      async (inputValue?: string): Promise<DropdownSelectOption[]> => {
        const { roles } = await getRolesBySearch(inputValue ? { name: inputValue } : {});

        const newRoleOptions: DropdownSelectOption[] = (roles || []).map((role: MidotRoleDTO) => ({
          label: role.name!,
          value: role.id!
        }));

        return newRoleOptions;
      },
      { promise: true }
    ),
    []
  );

  const loadRoleOptionsDebounced = useDebounceCallback(loadRoleOptions, DEBOUNCE_DELAY, {
    leading: true
  });

  const loadRoleOptionsByIds = useCallback(
    async (slugs?: string[]) => {
      const newRoleOptions = await loadRoleOptions();

      return filterByIds(newRoleOptions!, slugs);
    },
    [loadRoleOptions]
  );

  return (
    <DropdownSelect {...props} loadOptions={loadRoleOptionsDebounced} loadSelectedOptions={loadRoleOptionsByIds} />
  );
};

export default RoleDropdownSelect;
